import type { ApiClient } from "@commerce-os/api-client";
import { getMe, type MeResponse, updateMe, type UpdateMeRequest } from "@commerce-os/contracts";
import { isLocale } from "@commerce-os/i18n";
import { toast } from "@commerce-os/ui";
import { queryOptions, useMutation, useSuspenseQuery } from "@tanstack/react-query";

import { useApp } from "./context";
import { writeDevice } from "./device-storage";
import { describeError } from "./errors";
import { useT } from "./i18n";

export const meQueryKey = ["me"] as const;

/** The signed-in user, current tenant, tenants and permission keys (`GET /v1/auth/me`). */
export const meQuery = (api: ApiClient) =>
  queryOptions({ queryKey: meQueryKey, queryFn: () => api.call(getMe), staleTime: 60_000 });

/** Inside the signed-in layout, where `me` is always loaded first. */
export function useMe(): MeResponse {
  const { api } = useApp();
  return useSuspenseQuery(meQuery(api)).data;
}

/** Shows navigation for a permission key held at any location; the API remains the real check. */
export function useCan(): (permission: string | undefined) => boolean {
  const { permissions } = useMe();
  return (permission) => permission === undefined || permissions.includes(permission);
}

/** Held at every location, not only at some (tenant-wide actions such as main prices). UI only; the API decides. */
export function useCanEverywhere(): (permission: string) => boolean {
  const { allLocationPermissions } = useMe();
  return (permission) => allLocationPermissions.includes(permission);
}

/**
 * Changes language and/or digits. The language switches at once (no reload), then the choice is saved on the
 * user (`PATCH /v1/auth/me`) when signed in, and on the device for the sign-in pages.
 */
export function useChangePreferences() {
  const { api, queryClient, session, i18n } = useApp();
  const { t } = useT();
  return useMutation({
    mutationFn: async (change: UpdateMeRequest) => {
      if (change.locale !== undefined) {
        await i18n.changeLanguage(change.locale);
        writeDevice("locale", change.locale);
      }
      return session.token() === undefined ? undefined : api.call(updateMe, { body: change });
    },
    onSuccess: (me) => {
      if (me !== undefined) queryClient.setQueryData(meQueryKey, me);
    },
    onError: (error) => toast.error(describeError(t, error).message),
  });
}

/** Follows the signed-in user's saved language (it wins over the device's last choice). */
export async function followUserLocale(i18n: AppContextI18n, locale: string): Promise<void> {
  if (isLocale(locale) && i18n.language !== locale) {
    await i18n.changeLanguage(locale);
    writeDevice("locale", locale);
  }
}

type AppContextI18n = ReturnType<typeof useApp>["i18n"];
